import {
  ClientEvent,
  PROTOCOL_VERSION,
  type BossId,
  type PlayerRole,
  type ClientToServerEvents,
  type PlayerAction,
  type RoomMode,
  type ServerToClientEvents,
} from '@bioforge/protocol';
import { io, type Socket } from 'socket.io-client';
import { ActionSequencer } from './action-sequencer.js';

export type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Единственное Socket.IO-соединение клиента (SPEC.md, раздел 9). Экраны не создают своих сокетов,
 * а подписываются на события через этот класс.
 */
export class NetClient {
  readonly socket: GameSocket;
  private readonly sequencer = new ActionSequencer();

  constructor(url: string) {
    this.socket = io(url, {
      auth: { protocolVersion: PROTOCOL_VERSION, clientType: 'web' },
      transports: ['websocket'],
    });
  }

  createRoom(mode: RoomMode, name: string, bossId?: BossId): void {
    this.socket.emit(ClientEvent.RoomCreate, bossId ? { mode, name, bossId } : { mode, name });
  }

  /** Способности Versus перед матчем: слот C и слот V. */
  selectLoadout(c: 'cryo' | 'antibiotic', v: 'fever' | 'immunity'): void {
    this.socket.emit(ClientEvent.RoomSelectLoadout, { abilities: [c, v] });
  }

  joinRoom(roomCode: string, name?: string): void {
    this.socket.emit(ClientEvent.RoomJoin, name ? { roomCode, name } : { roomCode });
  }

  leaveRoom(): void {
    this.socket.emit(ClientEvent.RoomLeave, {});
  }

  /** Закрывает соединение (выход из сетевого экрана). */
  close(): void {
    this.socket.disconnect();
  }

  resume(resumeToken: string): void {
    this.socket.emit(ClientEvent.SessionResume, { resumeToken });
  }

  setReady(ready: boolean): void {
    this.socket.emit(ClientEvent.RoomReady, { ready });
  }

  selectRole(role: PlayerRole): void {
    this.socket.emit(ClientEvent.RoomSelectRole, { role });
  }

  requestStart(): void {
    this.socket.emit(ClientEvent.MatchRequestStart, {});
  }

  requestRematch(matchId: string): void {
    this.socket.emit(ClientEvent.MatchRequestRematch, { matchId });
  }

  /** Текущий матч, к которому относятся действия игрока. */
  setMatch(matchId: string | null): void {
    this.sequencer.setMatch(matchId);
  }

  /** Выбор Стратегом следующей капсулы (Co-op). */
  selectPiece(pieceOptionId: string): void {
    const envelope = this.sequencer.envelope();
    if (envelope) {
      this.socket.emit(ClientEvent.CoopSelectPiece, { ...envelope, pieceOptionId });
    }
  }

  sendAction(type: PlayerAction): void {
    const request = this.sequencer.next(type);
    if (request) {
      this.socket.emit(ClientEvent.GameAction, request);
    }
  }
}
